import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useActualIncidentId } from './commonHooks';
import axios from 'axios';
import { toastConfig } from 'assets/data/config';
import { apiPrefix } from 'constants/apiConfig';
import { ResourceStatus } from '../Resources/constants/resourceConstants';
import {
  CheckoffRecord,
  ResourceOrigin,
} from '../Resources/types/resourceTypes';
import { icsFormInputsKeys } from './icsFormInputsHooks';
import { keys as resourceSetKeys } from './resourceSetHooks';
import type { ActionAttachment } from '../actionTicket/actionTicketModel';

// Query Keys
type Filters = {
  current_status?: string;
  resource_type?: string;
  exclude_checked_out?: boolean;
};

export const resourcesInIncidentKeys = {
  base: ['resources-in-incident'] as const,
  byIncidentId: (incidentId: string) =>
    [...resourcesInIncidentKeys.base, incidentId] as const,
  byIncidentIdWithFilters: (incidentId: string, filters?: Filters) =>
    filters
      ? ([
          ...resourcesInIncidentKeys.byIncidentId(incidentId),
          filters,
        ] as const)
      : resourcesInIncidentKeys.byIncidentId(incidentId),
};

// Types
export interface GeoLocation {
  lat?: number;
  lng?: number;
  name?: string;
  address?: string;
}

export interface ResourceInIncident {
  id: string;
  resource_in_incident_id: string;
  resource_set_id: string;
  incident_id: string;
  group_guid: string;
  attachments?: ActionAttachment[];

  /**
   * `walk_up` for an asset documented at the check-in desk rather than
   * dispatched off the organization list.
   */
  origin?: ResourceOrigin;

  // ICS 211 Header Information (Items 1-3)
  operational_period_from?: string;
  operational_period_to?: string;

  // ICS 211 Check-in Data (Item 4)
  checkin_datetime: string;
  checkin_by_user_guid: string;

  // ICS 211 Resource Classification (Item 5)
  state_and_agency?: string;
  resource_category?: string;
  resource_kind?: string;
  resource_type?: string;
  resource_name_identifier?: string;
  strike_team_task_force?: string; // ST/TF/null

  // ICS 211 Resource Details (Items 6-14)
  order_request_number?: string;
  leader_name: string;
  personnel_count: number;
  incident_contact_info?: string;
  incident_lodging_info?: string;
  home_unit_agency?: string;
  departure_point?: string;
  departure_datetime?: string;
  travel_method?: string;
  work_assignment_id?: string;
  incident_assignment_location?: string;
  other_qualifications?: string;

  // Geolocation fields
  location_geolocation?: GeoLocation | null;
  resource_site_id?: string;
  departure_geolocation?: GeoLocation | null;
  departure_site_id?: string;
  assignment_geolocation?: GeoLocation | null;
  assignment_site_id?: string;
  destination_geolocation?: GeoLocation | null;
  destination_site_id?: string;

  // ICS 211 Data Transmission (Items 15-16)
  data_sent_to_resl_datetime?: string;
  data_sent_to_resl_initials?: string;
  prepared_by_name?: string;
  prepared_by_position?: string;
  prepared_by_signature?: string;
  prepared_datetime?: string;

  // ICS 219 T-Card Status Tracking
  current_status: ResourceStatus;
  assignment_details?: string;
  status_since: string;
  estimated_return_time?: string;
  status_notes?: string;
  last_status_update_by?: string;
  last_status_update_datetime?: string;

  // ICS 221/238 Demobilization Data
  checkout_datetime?: string;
  checkout_by_user_guid?: string;
  release_method?: string;
  checkout_destination?: string;
  checkout_eta_home?: string;
  checkout_travel_method?: string;
  checkout_contact_info?: string;
  checkout_remarks?: string;
  checkout_checkoffs?: Record<string, CheckoffRecord>;

  // Additional ICS 221 fields
  planned_release_datetime?: string;
  resource_personnel_released?: string;
  room_overnight?: boolean;
  estimated_departure_datetime?: string;
  actual_release_datetime?: string;
  estimated_arrival_datetime?: string;
  manifest_required?: boolean;
  manifest_number?: string;
  unit_agency_notified?: string;
  unit_agency_notified_bool?: boolean;
  unit_agency_notified_name?: string;

  // ICS 238 columns 16, 17 and 20
  is_excess?: boolean;
  excess_notes?: string;
  last_shift_datetime?: string;
  actual_arrival_datetime?: string;

  demob_prepared_by_name?: string;
  demob_prepared_by_position?: string;
  demob_prepared_by_signature?: string;
  demob_prepared_datetime?: string;

  /**
   * Set when this row is the auto-created resource standing in for a roster
   * seat assignment, which is what lets a roster member be matched to the
   * resource an ICS-204 can actually reference.
   */
  position_assignment_id?: string;

  /**
   * The roster seat this resource stands for. Set alongside
   * `position_assignment_id` for an occupied seat, and on its own for a seat an
   * ICS-204 planned while it was still vacant.
   */
  position_id?: string;

  // From joined resource_sets table
  resource_name?: string;
  resource_set_description?: string;
  resource_set_kind?: string;
  resource_set_type?: string;
  resource_set_quantity?: number;

  // Metadata
  version: number;
  created_datetime: string;
  updated_datetime: string;
}

/**
 * Everything a lifecycle write moves.
 *
 * The resource list and the organization's asset list obviously, the roster because a member's check-in status hangs
 * off it, and the form version queries: the server stamps the 211, 238 and 221 behind every one of these writes, so
 * a card showing "Last updated" or a version history would otherwise go stale until something else refetched it.
 */
function invalidateResourceLifecycle(
  queryClient: ReturnType<typeof useQueryClient>,
  incidentId: string
) {
  queryClient.invalidateQueries({
    queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
  });
  queryClient.invalidateQueries({ queryKey: resourceSetKeys.all });
  queryClient.refetchQueries({ queryKey: resourceSetKeys.all, type: 'active' });
  queryClient.invalidateQueries({ queryKey: ['incidentRoster', incidentId] });
  queryClient.invalidateQueries({ queryKey: ['icsFormTip'] });
  queryClient.invalidateQueries({ queryKey: ['icsFormTipsForPeriod'] });
  queryClient.invalidateQueries({ queryKey: ['icsFormVersions'] });
  queryClient.invalidateQueries({ queryKey: icsFormInputsKeys.all });
}

// Fetch resources in incident
export function useResourcesInIncident(filters?: Filters) {
  const incidentId = useActualIncidentId();

  return useQuery({
    queryKey: resourcesInIncidentKeys.byIncidentIdWithFilters(
      incidentId,
      filters
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('incident_id', incidentId);
      if (filters?.current_status)
        params.append('current_status', filters.current_status);
      if (filters?.resource_type)
        params.append('resource_type', filters.resource_type);
      if (filters?.exclude_checked_out)
        params.append(
          'exclude_checked_out',
          String(filters.exclude_checked_out)
        );

      const { data } = await axios.get<ResourceInIncident[]>(
        `${apiPrefix}/dice/ics/ics_resource_in_incident?${params}`
      );
      return data;
    },
    enabled: !!incidentId,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
}

// Add resource to incident (initial creation)
export function useCheckInResource() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async (data: Partial<ResourceInIncident>) => {
      const { data: result } = await axios.post(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/add-to-incident`,
        data
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
  });
}

/**
 * Resolves the resource standing for a roster seat, creating it when the seat
 * is vacant.
 *
 * An ICS-204 plans `ics_resource_in_incident` ids, and only a filled seat gets
 * one from the roster, so planning an empty position has to mint the record
 * first. Idempotent server-side: a seat that already has a resource returns
 * it.
 */
export function useCreatePositionResource() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async (positionId: string) => {
      const { data } = await axios.post<ResourceInIncident>(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/position-resource`,
        { incident_id: incidentId, position_id: positionId }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
    },
    onError: () => {
      toast.error('Failed to add the position as a resource.', toastConfig);
    },
  });
}

// Update resource check-in information
export function useUpdateResourceCheckIn() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/checkin`,
        data
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
  });
}

// Update resource status
export function useUpdateResourceStatus() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/status`,
        data
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
  });
}

// Check out resource
export function useCheckOutResource() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/checkout`,
        data
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
  });
}

/**
 * Check in a resource that is not on the organization's asset list.
 *
 * The server creates the asset and the check-in together, so the walk-up is a normal asset afterwards and the 211
 * row it prints has a lifecycle record behind it like any other.
 */
export function useManualCheckIn() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async (data: Partial<ResourceInIncident>) => {
      const { data: result } = await axios.post<ResourceInIncident>(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/manual-checkin`,
        { ...data, incident_id: incidentId }
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
    onError: () => {
      toast.error('Failed to record the check-in.', toastConfig);
    },
  });
}

/**
 * Amend a demobilization already under way: a later Block 6 sign-off, a travel detail, the Excess or Last Shift
 * columns of the 238. Only the fields sent are written, so one unit's sign-off cannot blank another's entries.
 */
export function useUpdateDemob() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put<ResourceInIncident>(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/demob`,
        data
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
    onError: () => {
      toast.error('Failed to update the demobilization.', toastConfig);
    },
  });
}

/** ICS 238 column 20: the resource reached its home unit. Refused server-side unless it has been released. */
export function useMarkArrived() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      actualArrivalDatetime,
    }: {
      id: string;
      actualArrivalDatetime?: string;
    }) => {
      const { data: result } = await axios.put<ResourceInIncident>(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/arrival`,
        { actual_arrival_datetime: actualArrivalDatetime }
      );
      return result;
    },
    onSuccess: () => {
      invalidateResourceLifecycle(queryClient, incidentId);
    },
    onError: () => {
      toast.error('Failed to mark the resource as arrived home.', toastConfig);
    },
  });
}

// Update resource work assignments (reciprocal relationship)
export function useUpdateResourceWorkAssignments() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      workAssignmentIds,
    }: {
      id: string;
      workAssignmentIds: string[];
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/work-assignments`,
        { work_assignment_ids: workAssignmentIds }
      );
      return result;
    },
    onSuccess: () => {
      // Invalidate both resources and work assignments queries
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      // Invalidate all work assignment queries regardless of user_guid
      queryClient.invalidateQueries({
        queryKey: ['incident_workAssignments', incidentId],
      });
    },
  });
}

// Delete resource from incident
export function useDeleteResourceInIncident() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axios.delete(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}`
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate all queries that start with 'resources-in-incident' and the incidentId
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      // Also invalidate resource sets to update the organization view
      queryClient.invalidateQueries({
        queryKey: ['resource_sets'],
      });
    },
  });
}
